<script setup lang="ts">
/**
 * What a signed-in but unconfirmed account sees instead of the game. The
 * backend's `EmailVerifiedGuard` refuses everything game-side until the link
 * in the inbox is clicked, so the one action that helps is shown: send it
 * again.
 *
 * On `AuthShell`'s steel card: a big letter, the address it went to in a chip,
 * resend as the yellow plate and sign out as a quiet plate under the card. The
 * dev link, when a stack has no mailer, is a hint well with the link in it.
 */
import { onMounted, ref } from 'vue';

import { useI18n } from '@/i18n';
import AuthShell from './AuthShell.vue';
import AuthNotice from './auth/AuthNotice.vue';
import AuthStatus from './auth/AuthStatus.vue';
import GameIcon from './kit/GameIcon.vue';
import ActionButton from './panel/ActionButton.vue';
import { useAccount } from '@/state/account';
import { askSignOut } from '@/state/confirm-sign-out';
import { useSession } from '@/state/session';

const session = useSession();
const account = useAccount();
const { t } = useI18n();

/** Resend is held this long after a send, so the button cannot be hammered (as before the rebuild). */
const RESEND_COOLDOWN_MS = 5000;

const devLink = ref<string | null>(null);
const cooling = ref(false);

onMounted(() => {
  account.loadAccount().catch(() => {});
});

async function resend(): Promise<void> {
  cooling.value = true;
  devLink.value = await account.resendVerification();
  window.setTimeout(() => {
    cooling.value = false;
  }, RESEND_COOLDOWN_MS);
}
</script>

<template>
  <AuthShell test-id="verify-gate" icon="letter" :title="t('verify.gateTitle')">
    <AuthStatus icon="letter" :title="t('verify.awaiting')" data-test="verify-status">{{ t('verify.gateLine') }}</AuthStatus>

    <p class="k-chip address">
      <GameIcon name="letter" :size="28" />
      <strong data-test="verify-address">{{
        account.profile.value?.email ?? session.player.value?.email ?? t('verify.yourAddress')
      }}</strong>
    </p>

    <ActionButton
      variant="primary"
      block
      size="lg"
      data-test="verify-resend"
      :disabled="account.busy.value || cooling"
      :loading="account.busy.value"
      @click="resend"
    >
      <GameIcon name="letter" :size="40" />
      {{ account.busy.value ? t('auth.working') : t('verify.resend') }}
    </ActionButton>

    <AuthNotice v-if="account.error.value" tone="error" data-test="verify-error">{{ account.error.value }}</AuthNotice>

    <AuthNotice v-if="devLink" data-test="verify-dev-link">
      {{ t('verify.devLink') }} <a :href="devLink">{{ devLink }}</a>
    </AuthNotice>

    <template #after>
      <ActionButton variant="danger" size="sm" data-test="sign-out" @click="askSignOut()">{{ t('common.signOut') }}</ActionButton>
    </template>
  </AuthShell>
</template>

<style scoped>
.address {
  align-self: center;
  max-width: 100%;
  padding: 0.35rem 0.9rem 0.35rem 0.5rem;
  margin: 0;
  font-family: var(--font-game-body);
  font-size: 0.95rem;
}

.address strong {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 600;
}

:deep(a) {
  color: var(--kit-gold);
  word-break: break-all;
}
</style>
